<script setup lang="ts">
import type { Form } from '~/ui-blocks/Formalization/blocks/paymentModel/Invoice/Invoice.schema'
import type { InvoiceFormOptions } from '~/ui-blocks/Formalization/blocks/paymentModel/Invoice/Invoice.types'
import type { EquipmentOrigin, InvoicesKindEnum, ReceiptModel } from '~/utils/receipt-model/ReceiptModel'
import { PaymentModelStatus } from '~/constants/receiptModel'
import InvoiceUploadField from '~/ui-blocks/Formalization/blocks/paymentModel/Invoice/InvoiceUploadField.vue'
import { canReplaceInvoice, useInvoiceDocuments } from '~/ui-blocks/Formalization/blocks/paymentModel/Invoice/useInvoiceDocuments'
import { CORRECTION_LETTER_FIELD_ID } from '~/ui-blocks/Formalization/blocks/paymentModel/paymentModel.constants'

// props
const props = defineProps<{
  form: Form
  data: ReceiptModel
  projectId: string
  options: InvoiceFormOptions
  disableField: boolean
  stepStatus: string | null
  equipmentOrigin: EquipmentOrigin
  isComboStoreOrder: boolean
  showErrorsAfterSubmit?: boolean
  equipmentInvoiceDisabled?: boolean
  // Terminal financing: uploads are view/download only (no POST/DELETE).
  readOnly?: boolean
}>()

// initial base hooks
const { t } = useI18n()

const { currentDocuments, loadingStates, updateDocument } = useInvoiceDocuments(
  props.form,
  () => props.data,
  () => props.projectId,
  t,
  () => props.readOnly,
)

async function handleDocumentChange(kind: InvoicesKindEnum, file: File | null): Promise<void> {
  if (props.readOnly)
    return

  await updateDocument(kind, file)
}

const correctionLetterError = computed(() =>
  props.readOnly ? undefined : props.form.errors.value.correction_letter,
)

// initial variables and computed
const isContainer = computed(() => props.equipmentOrigin === 'container')

const showCorrectionField = computed(() =>
  props.options.hasCorrectionLetter || Boolean(currentDocuments.correction_letter),
)

const showServiceProductInvoice = computed(() => props.options.isSingleInvoice || isContainer.value)

const productsErrorDisplay = computed(() =>
  props.showErrorsAfterSubmit ? props.form.errors.value.products : undefined,
)

const isEquipmentInvoiceLocked = computed(() => props.disableField || Boolean(props.equipmentInvoiceDisabled))

// With a document, `disabled` only hides Replace (View/Download stay), per `canReplaceInvoice`.
function isUploadDisabled(kind: InvoicesKindEnum, lockedWhenEmpty: boolean): boolean {
  const currentDocument = currentDocuments[kind]
  if (currentDocument)
    return !canReplaceInvoice(props.stepStatus, currentDocument.status, props.disableField)
  return lockedWhenEmpty
}

const REMINDER_STATUSES: string[] = [PaymentModelStatus.IN_PROGRESS, PaymentModelStatus.TO_REVIEW]

const isToReview = computed(() => props.stepStatus === PaymentModelStatus.TO_REVIEW)

const visibleInvoiceKinds = computed<InvoicesKindEnum[]>(() => {
  const kinds: InvoicesKindEnum[] = []
  if (props.options.isSeparateInvoices)
    kinds.push('products')
  if (showServiceProductInvoice.value)
    kinds.push('service_product')
  if (props.options.hasFinancingCommissionToReceive)
    kinds.push('commission')
  return kinds
})

const showSubmitReminder = computed(() => {
  const kinds = visibleInvoiceKinds.value
  return !props.readOnly
    && !props.isComboStoreOrder
    && REMINDER_STATUSES.includes(props.stepStatus ?? '')
    && kinds.length > 0
    && kinds.every(kind => Boolean(currentDocuments[kind]))
})

const submitReminderTitle = computed(() => (
  isToReview.value
    ? t('receipt-model.invoices.reminder.title_to_review')
    : t('receipt-model.invoices.reminder.title')
))

const submitReminderText = computed(() => {
  if (isToReview.value)
    return t('receipt-model.invoices.reminder.text_to_review')

  return visibleInvoiceKinds.value.length > 1
    ? t('receipt-model.invoices.reminder.text_plural')
    : t('receipt-model.invoices.reminder.text_single')
})
</script>

<template>
  <form
    class="invoice-form"
    data-testid="invoice-form"
    novalidate
    @submit.prevent
  >
    <h3 class="invoice-form__title">
      {{ t('receipt-model.invoices.title') }}
    </h3>

    <SolAlert
      v-if="showSubmitReminder"
      id="invoice-submit-reminder"
      feedback="warning"
      :title="submitReminderTitle"
    >
      {{ submitReminderText }}
    </SolAlert>

    <div v-show="showCorrectionField" class="invoice-form__correction">
      <div :id="CORRECTION_LETTER_FIELD_ID">
        <InvoiceUploadField
          id="correction_letter"
          :label="t('form.correction_letter')"
          :hint="t('form.correnction_letter.helper_text')"
          :approved-label="t('form.payment_model.correction_letter_approved')"
          :current-document="currentDocuments.correction_letter"
          :form-error="correctionLetterError"
          :loading="loadingStates.correction_letter"
          :disabled="isUploadDisabled('correction_letter', disableField)"
          @change="handleDocumentChange('correction_letter', $event)"
        />
      </div>

      <SolDivider thickness="x-small" orientation="horizontal" />
    </div>

    <SolAlert
      v-if="isContainer"
      id="project-invoice-alert"
      class="w-full"
      feedback="informative"
      :title="t('receipt-model.invoice.project_invoice_alert.title')"
    >
      <span v-html="t('receipt-model.invoice.project_invoice_alert.text')" />
    </SolAlert>

    <InvoiceUploadField
      v-if="options.isSeparateInvoices"
      id="products"
      :label="t('receipt-model.invoices.products_label')"
      :approved-label="t('form.payment_model.invoice_approved')"
      :current-document="currentDocuments.products"
      :form-error="productsErrorDisplay"
      :loading="loadingStates.products"
      :disabled="isUploadDisabled('products', isEquipmentInvoiceLocked)"
      @change="handleDocumentChange('products', $event)"
    />

    <InvoiceUploadField
      v-if="showServiceProductInvoice"
      id="service_product"
      :label="isContainer ? t('form.project_invoice') : t('form.equipment_invoice')"
      :approved-label="t('form.payment_model.invoice_approved')"
      :current-document="currentDocuments.service_product"
      :form-error="form.errors.value.service_product"
      :loading="loadingStates.service_product"
      :disabled="isUploadDisabled('service_product', disableField)"
      @change="handleDocumentChange('service_product', $event)"
    />

    <InvoiceUploadField
      v-if="options.hasFinancingCommissionToReceive"
      id="commission"
      :label="t('receipt-model.invoices.commission_label')"
      :hint="t('receipt-model.invoices.commission_hint')"
      :approved-label="t('form.payment_model.invoice_approved')"
      :current-document="currentDocuments.commission"
      :form-error="form.errors.value.commission"
      :loading="loadingStates.commission"
      :disabled="isUploadDisabled('commission', disableField)"
      @change="handleDocumentChange('commission', $event)"
    />
  </form>
</template>

<style lang="scss" scoped>
.invoice-form {
  @apply flex flex-col gap-4xs;

  // Fixed 20px (Grotesk): `fonts-heading-h3` grows to 24px from 1024px.
  &__title {
    @apply font-highlight font-regular text-xs leading-xs text-neutral-low-dark;
  }

  &__correction {
    @apply flex flex-col gap-4xs;
  }
}
</style>
